import { createSelector } from '@reduxjs/toolkit'
import React from 'react'

import { useAppSelector } from '@flecto-io/core'
import { type RootState } from '@flecto-io/core/src/store/redux'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { EBookingStatus } from '@flecto/types/src/enums/EBookingStatus'
import BookingInsurance from '@flecto/ui/@shared/Insurances/InsuranceCard'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import InsuranceProvider, { useInsuranceManagerContext } from '../../provider'
import { useInsuranceManagerState } from '../../state'
import { BookingInsuranceManager } from '../InsuranceManager'
import useStatus from './hooks/useStatus'
import useStatusIcon from './hooks/useStatusIcon'
import useStatusLabel from './hooks/useStatusLabel'

interface IBookingInsuranceModule {
  onClick?: () => void
}

const BookingInsuranceModule = ({ onClick }: IBookingInsuranceModule) => {
  const {
    booking: { data },
  } = useInsuranceManagerContext()
  const { setModalOpened } = useInsuranceManagerState()
  const isBookingIsCanceled = data?.status === EBookingStatus.CANCELED
  const isBookingIsActive = Boolean(data?.internal_id)
  const { isTablet } = useMediaQuery()
  const { data: user } = useQueryGetUserLogged()
  const inUserInsuranceActive = user?.meta.insured

  const { insuranceButton } = useStatus()

  const insuranceState = createSelector(
    (state: RootState) => ({
      insurances: state.bookings.booking.slices.insurances,
      items: state.items.items.data,
    }),
    (rawData) => {
      const { insurances } = rawData

      return {
        insuranceMeta: insurances.meta,
      }
    }
  )

  const { insuranceMeta } = useAppSelector(insuranceState)

  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const label = useStatusLabel()
  const icon = useStatusIcon()

  const isShowButtonDisable: boolean = React.useMemo(() => {
    if (insuranceMeta.count === 0 && isBookingIsCanceled) {
      return true
    }

    if (!isBookingIsActive) {
      return true
    }
    return false
  }, [isBookingIsActive, insuranceMeta, isBookingIsCanceled])

  if (!inUserInsuranceActive) {
    return null
  }

  const handleClick = (): void => {
    if (!isTablet) setModalOpened(true)
    onClick?.()
  }

  return (
    <>
      <BookingInsuranceManager />

      <BookingInsurance
        isLoading={false}
        status={{
          title: t('title'),
          description: label,
          icon: icon,
        }}
        button={{
          onClick: handleClick,
          loading: insuranceMeta.meta_status === 'pending',
          disabled: isShowButtonDisable,
          variant: insuranceButton.variant,
          display: isBookingIsActive ? 'block' : 'none',
          children: t(insuranceButton.textKey),
        }}
      />
    </>
  )
}

export default function BookingInsuranceModuleWrapper(
  props: React.ComponentProps<typeof BookingInsuranceModule>
) {
  return (
    <InsuranceProvider>
      <BookingInsuranceModule {...props} />
    </InsuranceProvider>
  )
}
